import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import { IconArrowRight, IconPackage, IconHeart } from '../../components/common/Icons/Icons';
import { formatCurrency } from '../../utils/currency';
import './Account.css';

const API_URL  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';

function AccountDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Retry logic with exponential backoff
  const fetchRecentOrders = useCallback(async (attempt = 0) => {
    if (!user || !token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Allow for backend cold start

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/account/orders?limit=3`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (controller.signal.aborted) return;

      // Handle 401 - session expired
      if (response.status === 401) {
        logout();
        navigate('/login', {
          replace: true,
          state: { reason: 'SESSION_EXPIRED' },
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (controller.signal.aborted) return;

      if (data.success) {
        setRecentOrders(data.orders || []);
        setError(null);
        setRetryCount(0);
      } else {
        setRecentOrders([]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (controller.signal.aborted) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        // Retry logic: up to 3 attempts with exponential backoff
        const maxRetries = 3;
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.warn(`[AccountDashboard] Fetch failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          
          setTimeout(() => {
            if (!cancelled) {
              setRetryCount(prev => prev + 1);
              fetchRecentOrders(attempt + 1);
            }
          }, delay);
          return;
        }

        // All retries failed
        setError('Failed to load recent orders. Please refresh the page.');
        if (process.env.NODE_ENV === 'development') {
          console.error('[AccountDashboard] fetch error:', err);
        }
        setRecentOrders([]);
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, token, navigate, logout, API_URL]);

  useEffect(() => {
    const cleanup = fetchRecentOrders(0);
    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchRecentOrders]);

  const handleRetry = useCallback(() => {
    setError(null);
    setRetryCount(0);
    setLoading(true);
    fetchRecentOrders(0);
  }, [fetchRecentOrders]);

  return (
    <>
      <Helmet>
        <title>My Account | {BRAND_NAME}</title>
        <meta name="description" content={`Manage your ${BRAND_NAME} account, view orders, addresses, and preferences.`} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account`} />
      </Helmet>
      
      <Layout showFooter={true} showBottomTab={true}>
        <div className="account-page">
        <div className="account-container">
          <div className="account-header">
            <h1 className="account-title">My Account</h1>
            <p className="account-subtitle">Welcome back, {user?.firstName || user?.name || 'User'}</p>
          </div>

          <div className="account-grid">
            {/* Profile Summary */}
            <div className="account-card">
              <h2 className="account-card__title">Profile</h2>
              <div className="account-card__content">
                <div className="account-profile">
                  <div className="account-profile__avatar">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name || user.email} />
                    ) : (
                      <span className="account-profile__initial">
                        {((user?.firstName || user?.name || '').charAt(0) + (user?.lastName || '').charAt(0)).toUpperCase() || (user?.email || '').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="account-profile__info">
                    <h3 className="account-profile__name">
                      {user?.firstName && user?.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : user?.name || user?.email?.split('@')[0] || 'User'}
                    </h3>
                    <p className="account-profile__email">{user?.email}</p>
                    {user?.mobileNumber && (
                      <p className="account-profile__mobile">{user.mobileNumber}</p>
                    )}
                  </div>
                </div>
                <Link to="/account/settings" className="account-card__link">
                  Edit Profile <IconArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="account-card">
              <h2 className="account-card__title">Quick Actions</h2>
              <div className="account-card__content">
                <div className="account-actions">
                  <Link to="/account/orders" className="account-action">
                    <IconPackage size={24} />
                    <span>My Orders</span>
                  </Link>
                  <Link to="/account/wishlist" className="account-action">
                    <IconHeart size={24} />
                    <span>Saved Pieces</span>
                  </Link>
                  <Link to="/account/addresses" className="account-action">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>Addresses</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="account-card account-card--full">
              <div className="account-card__header">
                <h2 className="account-card__title">Recent Orders</h2>
                <Link to="/account/orders" className="account-card__link">
                  View All <IconArrowRight size={14} />
                </Link>
              </div>
              <div className="account-card__content">
                {error && !loading ? (
                  <div className="account-error" role="alert">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="account-error__icon" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p className="account-error__message">{error}</p>
                    <button
                      onClick={handleRetry}
                      className="account-error__retry btn-primary"
                      aria-label="Retry loading orders"
                    >
                      Retry
                    </button>
                  </div>
                ) : loading ? (
                  <div className="account-loading">
                    <div className="account-loading__skeleton">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="account-loading__skeleton-item">
                          <div className="account-loading__skeleton-line shimmer" />
                          <div className="account-loading__skeleton-line shimmer" />
                          <div className="account-loading__skeleton-line shimmer" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : recentOrders.length > 0 ? (
                  <div className="account-orders">
                    {recentOrders.map((order) => (
                      <div key={order._id} className="account-order">
                        <div className="account-order__info">
                          <p className="account-order__number">Order #{order.orderNumber}</p>
                          <p className="account-order__date">
                            {new Date(order.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="account-order__status">
                          <span className={`account-order__status-badge account-order__status-badge--${order.status}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="account-order__total">
                          {formatCurrency(order.pricing?.total || order.total || 0)}
                        </div>
                        <Link to={`/account/orders/${order._id}`} className="account-order__link">
                          View Details <IconArrowRight size={14} />
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="account-empty">
                    <p>No orders yet</p>
                    <Link to="/shop" className="account-empty__link">
                      Start Shopping <IconArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </Layout>
    </>
  );
}

export default AccountDashboard;
